import { Alert, Box, Stack } from '@mui/material';
import { RHFTextField } from 'src/components/hook-form';
import { translateKey } from 'src/locales/translate';
import { GetMedicineDto } from 'woofwoof-api';
import MedicationClinicalSection from './MedicationClinicalSection';
import MedicationDosageSection from './MedicationDosageSection';
import MedicationFormSummary from './MedicationFormSummary';
import MedicationIdentitySection from './MedicationIdentitySection';
import MedicationReminderSection from './MedicationReminderSection';
import MedicationStatusSection from './MedicationStatusSection';
import MedicationStockIssueDefault from './MedicationStockIssueDefault';
import MedicationStockSection from './MedicationStockSection';
import { autofillPickFrom } from './dosageAutofill';
import {
  MEDICATION_FORM_GRID,
  SPAN_FULL,
  SPAN_WIDE,
} from './medicationFormLayout';
import { MedicationStepId } from './medicationFormSteps';
import { StockSectionVariant } from './medicationStockView';
import { DosageAutofill } from './useDosageAutofill';

interface MedicationFormStepBodyProps {
  stepId?: MedicationStepId;
  disabled?: boolean;
  onMedicineChange: (medicine: GetMedicineDto | null) => void;
  selectedMedicine: GetMedicineDto | null;
  canEditReminder: boolean;
  singleReminderOnly?: boolean;
  isViewOnly: boolean;
  hasLinkedReminder: boolean;
  /** A medication being added has no course to report on or status to set. */
  isEditing: boolean;
  creatorName?: string;
  creatorOrganizationName?: string;
  creatorAvatar?: string;
  hideLifecycle?: boolean;
  hideReminder?: boolean;
  /** Facility screens only: the "Magazyn" step (#692 rev B). */
  stockEnabled?: boolean;
  onRequestStockDemand?: (productId: string) => void;
  /** #690: the details panel's "Zgłoś zapotrzebowanie" for a medicine the Warehouse lacks. */
  onRequestNamedDemand?: (name: string) => void;
  /** What the section edits: a Medication's taking or a treatment row (F-7). */
  stockVariant?: StockSectionVariant;
  /** #688 R1-2: the owner's address already on screen, for a new Rx supply. */
  stockOwnerAddress?: string;
  /**
   * #692: the modal's dosage autofill — fed by picks in the medicine step,
   * said above the dosage fields. Absent where nothing is prefilled (the
   * owner's tab).
   */
  autofill?: DosageAutofill;
  /** #692 §4: a treatment line being edited keeps its drug; its name is shown read-only. */
  medicineLocked?: boolean;
  /** #692: bumped by the host each time it resets the form for a line. */
  formSession?: number;
}

/** The fields of whichever step the vet is on, in the form's shared grid. */
export default function MedicationFormStepBody({
  stepId,
  disabled,
  onMedicineChange,
  selectedMedicine,
  canEditReminder,
  singleReminderOnly = false,
  isViewOnly,
  hasLinkedReminder,
  isEditing,
  creatorName,
  creatorOrganizationName,
  creatorAvatar,
  hideLifecycle,
  hideReminder,
  stockEnabled = false,
  onRequestStockDemand,
  onRequestNamedDemand,
  stockVariant,
  stockOwnerAddress,
  autofill,
  medicineLocked = false,
  formSession,
}: MedicationFormStepBodyProps) {
  return (
    <Stack sx={{ pb: 2 }}>
      <Box sx={MEDICATION_FORM_GRID}>
        {/* #692 rev B: outside the steps, so the computed issue follows a
            dose typed two steps before "Magazyn". */}
        {stockEnabled && (
          <MedicationStockIssueDefault
            disabled={disabled}
            session={formSession}
          />
        )}
        {stepId === 'medicine' && (
          <MedicationIdentitySection
            disabled={disabled}
            locked={medicineLocked}
            onMedicineChange={onMedicineChange}
            selectedMedicine={selectedMedicine}
            creatorName={creatorName}
            creatorOrganizationName={creatorOrganizationName}
            creatorAvatar={creatorAvatar}
            stockEnabled={stockEnabled}
            onRequestNamedDemand={onRequestNamedDemand}
            onPick={
              autofill
                ? (option) => autofill.applyPick(autofillPickFrom(option))
                : undefined
            }
            onIdentityCleared={autofill?.clear}
          />
        )}

        {stepId === 'dosage' && (
          <>
            <MedicationDosageSection
              disabled={disabled}
              autofillNotice={autofill?.notice ?? null}
              onClearAutofill={autofill?.clear}
            />
            <Box sx={SPAN_WIDE}>
              <RHFTextField
                name="notes"
                label="Dodatkowe notatki"
                multiline
                rows={3}
                disabled={disabled}
                fullWidth
              />
            </Box>
          </>
        )}

        {stepId === 'course' && (
          <MedicationClinicalSection
            disabled={disabled}
            showAdverseReaction={isEditing}
          />
        )}

        {stepId === 'status' && <MedicationStatusSection disabled={disabled} />}

        {stepId === 'stock' && stockEnabled && (
          <MedicationStockSection
            disabled={disabled}
            onRequestDemand={onRequestStockDemand}
            variant={stockVariant}
            ownerAddress={stockOwnerAddress}
          />
        )}

        {stepId === 'summary' && (
          <MedicationFormSummary
            hideLifecycle={hideLifecycle}
            hideReminder={hideReminder}
            showStatus={isEditing && !hideLifecycle}
            creatorName={creatorName}
            creatorOrganizationName={creatorOrganizationName}
            showStock={stockEnabled}
          />
        )}

        {stepId === 'reminder' && (
          <>
            {/* Locked with the other steps in flight and while unconfirmed (R3-4). */}
            <MedicationReminderSection
              canEditReminder={canEditReminder && !disabled}
              singleReminderOnly={singleReminderOnly}
            />
            {!canEditReminder && !isViewOnly && hasLinkedReminder && (
              <Alert severity="info" sx={SPAN_FULL}>
                {translateKey(
                  'hardcoded.mozesz_edytowac_tylko_przypomnienia_dodane_w_ramach_twojej',
                )}
              </Alert>
            )}
          </>
        )}
      </Box>
    </Stack>
  );
}
